import { LineChart, Line, ResponsiveContainer, YAxis, ReferenceLine, ReferenceArea } from 'recharts';
import type { SwingTradeAdjustement, LongTermAdjustement } from '@/models';
import type { GraphPoint } from '@/modelsAPI';

// Chart styling constants
const CHART_COLORS = {
  positive: '#10b981',
  negative: '#ef4444',
  averagePrice: '#6b7280',
  takeProfitLine: '#10b981',
  priceLine: '#3b82f6',
  strikeLine: '#ef4444',
  dotStroke: '#ffffff'
} as const;

const CHART_CONFIG = {
  paddingRatio: 0.1,
  strikeHeightRatio: 0.9,
  strikePaddingAbove: 0.05,
  dotRadius: 4,
  dotStrokeWidth: 1.5
} as const;

interface MinimalPriceChartProps {
  points: GraphPoint[];
  strikePrice: number;
  height?: number | `${number}%`;
  adjustements?: (SwingTradeAdjustement | LongTermAdjustement)[];
  averagePrice?: number;
  takeProfit?: number;
  /** Nur die Preislinie rendern, ohne Referenzlinien/-flächen und Punkte (z.B. als Hintergrund) */
  plain?: boolean;
}

interface ChartDataPoint {
  price: number;
  timestamp: number;
  adjustment?: {
    delta: number;
    price: number;
  };
}

// Helper function to find the closest data point by timestamp (unix seconds)
function findClosestDataPointIndex(chartData: ChartDataPoint[], targetTimestamp: string): number {
  const targetTime = new Date(targetTimestamp).getTime();
  let closestIndex = 0;
  let closestDiff = Infinity;

  chartData.forEach((point, index) => {
    const pointTime = point.timestamp * 1000; // Convert unix seconds to ms
    const diff = Math.abs(pointTime - targetTime);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = index;
    }
  });

  return closestIndex;
}

// Helper function to integrate adjustements into chart data
function integrateAdjustements(chartData: ChartDataPoint[], adjustements: (SwingTradeAdjustement | LongTermAdjustement)[]): void {
  adjustements.forEach((adj) => {
    const closestIndex = findClosestDataPointIndex(chartData, adj.timestamp);

    if (chartData[closestIndex] !== undefined) {
      chartData[closestIndex].adjustment = {
        delta: 'delta' in adj ? adj.delta : 0,
        price: adj.price / 100 // Convert cents to dollars
      };
    }
  });
}

// Helper function to calculate Y-axis domain with padding
function calculateSimpleDomain(minPrice: number, maxPrice: number): { yMin: number; yMax: number } {
  const range = maxPrice - minPrice;
  const padding = range * CHART_CONFIG.paddingRatio;
  return {
    yMin: minPrice - padding,
    yMax: maxPrice + padding
  };
}

// Helper function to calculate Y-axis domain for strike price charts
function calculateStrikeDomain(minPrice: number, maxPrice: number, strikePrice: number): { yMin: number; yMax: number } {
  if (minPrice < strikePrice) {
    return calculateSimpleDomain(minPrice, maxPrice);
  }

  const priceRange = maxPrice - strikePrice;
  const totalRange = priceRange / CHART_CONFIG.strikeHeightRatio;
  return {
    yMin: strikePrice - (totalRange * CHART_CONFIG.paddingRatio),
    yMax: maxPrice + (priceRange * CHART_CONFIG.strikePaddingAbove)
  };
}

export default function MinimalPriceChart({ points, strikePrice, height = 80, adjustements, averagePrice, takeProfit, plain = false }: MinimalPriceChartProps) {
  const isSwingMode = !plain && adjustements !== undefined && averagePrice !== undefined;

  // Transform GraphPoint data for chart
  // Backend returns data in chronological order, no need to reverse
  const chartData: ChartDataPoint[] = points
    .filter((point) => point.p !== null)
    .map((point) => ({
      price: point.p / 100, // CurrencyCent to decimal
      timestamp: point.t
    }));

  if (chartData.length === 0) {
    return null;
  }

  // Integrate adjustements into chart data for swing mode
  if (isSwingMode && adjustements) {
    integrateAdjustements(chartData, adjustements);
  }

  // Calculate Y-axis domain
  const prices = chartData.map(d => d.price);

  // Include takeProfit in price range calculation for swing mode
  const allPrices = isSwingMode && takeProfit !== undefined
    ? [...prices, takeProfit]
    : prices;

  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);

  const { yMin, yMax } = (isSwingMode || plain)
    ? calculateSimpleDomain(minPrice, maxPrice)
    : calculateStrikeDomain(minPrice, maxPrice, strikePrice);

  // Custom dot renderer for adjustments in swing mode
  const renderDot = isSwingMode
    ? (props: { cx?: number; cy?: number; payload?: ChartDataPoint }) => {
        const { cx, cy, payload } = props;

        if (!payload?.adjustment) {
          return <></>;
        }

        const color = payload.adjustment.delta >= 0
          ? CHART_COLORS.positive
          : CHART_COLORS.negative;

        return (
          <circle
            cx={cx}
            cy={cy}
            r={CHART_CONFIG.dotRadius}
            fill={color}
            stroke={CHART_COLORS.dotStroke}
            strokeWidth={CHART_CONFIG.dotStrokeWidth}
          />
        );
      }
    : false;

  return (
    <div className="pointer-events-none select-none w-full h-full">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={chartData}
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="strikeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLORS.strikeLine} stopOpacity={0.3} />
              <stop offset="100%" stopColor={CHART_COLORS.strikeLine} stopOpacity={0.1} />
            </linearGradient>
          </defs>

          <YAxis
            domain={[yMin, yMax]}
            hide={true}
          />

          {!isSwingMode && !plain && (
            <>
              <ReferenceArea
                y1={strikePrice}
                y2={yMin}
                fill="url(#strikeGradient)"
                fillOpacity={1}
                ifOverflow="hidden"
              />

              <ReferenceLine
                y={strikePrice}
                stroke={CHART_COLORS.strikeLine}
                strokeDasharray="5 5"
                strokeWidth={1}
              />
            </>
          )}

          {isSwingMode && averagePrice !== undefined && (
            <ReferenceLine
              y={averagePrice}
              stroke={CHART_COLORS.averagePrice}
              strokeDasharray="3 3"
              strokeWidth={1.5}
            />
          )}

          {isSwingMode && takeProfit !== undefined && (
            <ReferenceLine
              y={takeProfit}
              stroke={CHART_COLORS.takeProfitLine}
              strokeDasharray="3 3"
              strokeWidth={1.5}
            />
          )}

          <Line
            type="monotone"
            dataKey="price"
            stroke={CHART_COLORS.priceLine}
            strokeWidth={2}
            dot={renderDot}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
